import { nextTick, ref } from "vue";
import { type LocaleMessageDictionary, type VueMessageType, createI18n } from "vue-i18n";
import { gqlUri } from "./config";
import SUPPORT_LOCALES from "./i18n_supported_locales.json";

const DEFAULT_LOCALE = "en";
const LOCALE_OVERRIDE_COOKIE = "i18n_locale";

export const SUPPORTED_LOCALES: ReadonlyArray<string> = SUPPORT_LOCALES;

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match?.[1];
}

function getCookieI18nDisabled(): boolean {
  return getCookie("i18n_disable") === "1";
}

export const i18nDisabled = ref(getCookieI18nDisabled());

/** Unsupported locales are treated as "no override" rather than being applied. */
function supportedOrNull(locale: string | null | undefined): string | null {
  return locale && SUPPORT_LOCALES.includes(locale) ? locale : null;
}

/**
 * Admin-only override of the locale that would otherwise be derived from the
 * route (NGP) or the `webinterface_locale` cookie (WI embedded). Consumed by
 * `getCurrentLocale()`, so it takes effect everywhere the UI locale is used.
 */
export const localeOverride = ref(supportedOrNull(getCookie(LOCALE_OVERRIDE_COOKIE)));

export function setLocaleOverride(locale: string | null) {
  const override = supportedOrNull(locale);
  localeOverride.value = override;
  document.cookie = override
    ? `${LOCALE_OVERRIDE_COOKIE}=${override}; path=/`
    : `${LOCALE_OVERRIDE_COOKIE}=; path=/; max-age=0`;
}

export function toggleI18nDisabled() {
  i18nDisabled.value = !i18nDisabled.value;
  document.cookie = `i18n_disable=${i18nDisabled.value ? "1" : "0"}; path=/`;
  // Force vue-i18n to re-evaluate all translations by toggling the locale
  const currentLocale = i18n.global.locale.value;
  i18n.global.locale.value = "";
  nextTick(() => {
    i18n.global.locale.value = currentLocale;
  });
}

export const i18n = createI18n({
  locale: DEFAULT_LOCALE,
  legacy: false,
  globalInjection: true,
  missing: (_locale, key) => (i18nDisabled.value || import.meta.env.DEV ? key : ""),
  postTranslation: (translated, key) => (i18nDisabled.value ? key : translated),
});

export function setI18nLanguage(locale: string) {
  i18n.global.locale.value = locale;
  /**
   * NOTE:
   * If you need to specify the language setting for headers, such as the `fetch` API, set it here.
   * The following is an example for axios.
   *
   * axios.defaults.headers.common['Accept-Language'] = locale
   */
  document.querySelector("html")?.setAttribute("lang", locale);
}

setI18nLanguage(DEFAULT_LOCALE);

interface LoadedLocale {
  // Keep general messages to be able to merge them with game specific messages
  generalMessages: LocaleMessageDictionary<VueMessageType>;
  gameMessages: Map<string, LocaleMessageDictionary<VueMessageType>>;
}

// Mapping of locale name (e.g. "en")
const loadedLocales = new Map<string, LoadedLocale>();

export async function loadLocaleMessages(locale: string, gameKey?: string) {
  const baseUrl = `${gqlUri}i18n/`;
  // __CACHE_BUST__ is replaced by rollup on build
  const url = gameKey ? `${baseUrl}${locale}/${gameKey}.json` : `${baseUrl}${locale}.json`;
  return await fetch(url + "?__CACHE_BUST__").then((response) => response.json());
}

const loadedGameKey = new Map<string, string | undefined>();

async function ensureLoaded(locale: string, gameKey?: string, extraGameTranslationKeys?: Array<string>) {
  // load locale messages
  let loadedLocale;
  if (loadedLocales.has(locale)) {
    loadedLocale = loadedLocales.get(locale);
  } else {
    const generalMessages = await loadLocaleMessages(locale);
    loadedLocale = {
      generalMessages,
      gameMessages: new Map(),
    };
    loadedLocales.set(locale, loadedLocale);
  }
  if (loadedLocale) {
    // will always be defined, just TS control flow detection stupidity
    if (gameKey && !loadedLocale?.gameMessages.has(gameKey)) {
      let gameMessages = await loadLocaleMessages(locale, gameKey);
      if (extraGameTranslationKeys) {
        for (const extraGameTranslationKey of extraGameTranslationKeys) {
          // avoid loading the same translation twice
          if (gameKey !== extraGameTranslationKey) {
            gameMessages = { ...gameMessages, ...(await loadLocaleMessages(locale, extraGameTranslationKey)) };
          }
        }
      }
      loadedLocale?.gameMessages.set(gameKey, gameMessages);
    }
    // set locale and locale message if necessary
    if (!loadedGameKey.get(locale) || (gameKey && loadedGameKey.get(locale) !== gameKey)) {
      const messages = gameKey
        ? { ...loadedLocale.generalMessages, ...loadedLocale.gameMessages.get(gameKey) }
        : loadedLocale.generalMessages;
      i18n.global.setLocaleMessage(locale, messages);
      loadedGameKey.set(locale, gameKey);
    }
  }
}

export async function ensureLocale(locale: string, gameKey?: string, extraGameTranslationKeys?: Array<string>) {
  let localeToLoad = locale;
  if (!SUPPORT_LOCALES.includes(locale)) {
    localeToLoad = DEFAULT_LOCALE;
  }
  const localesToLoad = [locale];
  if (localeToLoad !== DEFAULT_LOCALE) {
    // Always load default locale too so fallbacks work
    localesToLoad.push(DEFAULT_LOCALE);
  }
  if (i18n.global.locale.value !== localeToLoad) {
    // set i18n language
    setI18nLanguage(localeToLoad);
  }
  await Promise.all(localesToLoad.map((l) => ensureLoaded(l, gameKey, extraGameTranslationKeys)));
  await nextTick();
}
